/**
 * The per-widget font-size rule, emitted for BOTH mount shapes (#1800).
 *
 * Every widget used to size itself with `:root, :host { font-size: Npx }`.
 * Inside a shadow root `:root` matches nothing, so the `:root` half looked
 * inert — but a widget's <style> is not always inside a shadow root. It sits in
 * the CUSTOMER page's light DOM in SSR bucket 'l' (no Declarative Shadow DOM —
 * Chrome on iPhone, for one) until the script mounts, and forever with JS off.
 * There `:root` IS the customer's <html>: the widget resized their whole page.
 * That is why `:root` was dropped.
 *
 * Dropping it alone would have made the font size a silent no-op everywhere
 * `:host` does not apply, so this re-adds the light-DOM half — scoped to the
 * one host element instead of the document. Two separate rules, never a merged
 * selector list:
 *
 *   :host {
 *     font-size: 18px;
 *     background-color: transparent !important;
 *   }
 *   [data-hr-widget][id="<widgetId>"]:not(:empty),
 *   [data-hr-widget][id^="<widgetId>:"]:not(:empty) {
 *     font-size: 18px;
 *   }
 *
 * Every clause is load-bearing:
 *
 * - The transparent declaration stays in a rule whose selector is EXACTLY
 *   `:host`. `cancelsHostPaint()` in test/widgets/host-background-contract.test.tsx
 *   splits the selector list on ',' and requires a bare `:host` member, so a
 *   merged list would read as a widget that no longer cancels the host paint.
 *
 * - The light arm carries font-size ONLY — never a background. A transparent
 *   light arm would cancel the deliberate #691 paint in styles/common.css
 *   (`[data-hr-theme="dark"]:not([data-hr-widget-category="layout"])`) and WIN,
 *   because common.css lives in `@layer base` while this inline CSS is
 *   unlayered. That is the #1567 failure mode on no-shadow mounts.
 *
 * - Two id arms, not one. The host element's id is `<widgetId>:<feedId>` from
 *   SSR (packages/homerunner-renderer/lib/render-utils.tsx), from the embed
 *   kit's `HR.box`, and from the #1741 WidgetIsland — but the dashboard's
 *   single-embed snippet emits a BARE `<widgetId>`. `#<id>` cannot be used at
 *   all: the ':' in the id parses as a pseudo-class.
 *
 * - Per widget id, which is per embed only while a widget id appears once on
 *   the page. `[data-hr-widget="explorer"]` would be strictly worse — it would
 *   cross-apply between two DIFFERENT explorer embeds — but the host carries no
 *   per-instance handle the light DOM can select: SSR varies only
 *   `data-hr-ssr-id` (render-utils.tsx `instanceKey`), and the embed kit and the
 *   dashboard snippet do not emit that attribute at all. So when a layout binds
 *   the SAME widget into two slots with different `font.size` overrides, both
 *   hosts carry the same `id` and, before either mounts, the later <style> in
 *   document order sizes both. Verified in Chromium. Accepted: it is bounded by
 *   the pre-mount window, and on `develop` the same page had the two `:root`
 *   arms fighting over the customer's <html> instead, which was worse.
 *
 * - `:not(:empty)` is what keeps that collision from outliving the pre-mount
 *   window, and it is NOT cosmetic. Cascade order compares tree context before
 *   specificity (CSS Scoping §3.2: between shadow trees, a NORMAL declaration
 *   from the outer tree wins), so a light-DOM arm in the document beats a
 *   `:host` rule living inside a shadow root — no specificity trick changes
 *   that, as a (0,4,1) `:host(...)` probe confirmed. Without the guard, one
 *   twin that has not mounted yet would override a twin that already has, and
 *   if that twin's mount THROWS (`data-hr-error="mount-failed"`,
 *   widget-core/src/runtime/mount.tsx) the healthy widget would keep the broken
 *   one's font size for good. `migrateSSRContent` empties the host when it moves
 *   the SSR markup into the shadow root, so `:not(:empty)` is false for exactly
 *   the hosts that have a working `:host` rule, and true for exactly the ones
 *   that do not: light-DOM SSR before mount, JS off, `shadowDOM: false`, a
 *   failed `setupShadowDOM`, and #1741's WidgetIsland.
 *
 * No `!important` is needed and the handoff needs no cleanup: `migrateSSRContent`
 * (packages/homerunner-widget-core/src/runtime/shadow-dom.ts) MOVES the <style>
 * into the shadow root at mount rather than cloning it, and a selector inside a
 * shadow tree cannot match its own host — so the light arm goes inert at exactly
 * the moment `:host` starts working.
 */
import { safeCssAttrValue } from "./css-safe";

/**
 * Emit the `:host` font-size rule plus its light-DOM twin.
 *
 * `cancelHostPaint` is an options flag rather than a second export because the
 * two widgets that opt out (destination-search, reviews) are the surprise, and
 * the flag names the surprise at the call site — the same way `safeCssColor`
 * keeps its one variation (`fallback`) in the signature instead of forking.
 *
 * An unusable `widgetId` emits the `:host` rule ALONE. Failing safe matters
 * more than the font size here: a half-escaped id in an attribute selector is
 * a CSS-injection sink on every public embed (the #904 class of bug).
 */
export function hostFontSizeCss(
  widgetId: unknown,
  fontSize: number,
  opts: { cancelHostPaint?: boolean } = {},
): string {
  const host =
    opts.cancelHostPaint === false
      ? `:host {
  font-size: ${fontSize}px;
}`
      : `:host {
  font-size: ${fontSize}px;
  background-color: transparent !important;
}`;

  const id = safeCssAttrValue(widgetId);
  if (id === "") return host;

  return `${host}
[data-hr-widget][id="${id}"]:not(:empty),
[data-hr-widget][id^="${id}:"]:not(:empty) {
  font-size: ${fontSize}px;
}`;
}
